'use client';

import { forwardRef, type ButtonHTMLAttributes } from 'react';

import {
  RadixTooltip,
  RadixTooltipContent,
  RadixTooltipProvider,
  RadixTooltipTrigger,
  TooltipSubText,
  TooltipText,
} from '../../../../components/ui/tooltip';
import { cn } from '../../../../lib/utils';
import type { TooltipPosition } from '../../feedback-and-status/tooltip/Tooltip';
import { useToggleGroup } from './ToggleGroupContext';

export interface ToggleItemProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  /** 그룹 내 고유 식별 값 */
  value: string;
  /** 아이콘 노드 */
  children?: React.ReactNode;
  /** 텍스트 라벨 (설정 시 아이콘 + 텍스트 레이아웃) */
  label?: string;
  /** 아이콘 표시 여부 (label과 함께 사용, 기본 true) */
  showIcon?: boolean;
  /** hover 시 툴팁 표시 여부 */
  isShowTooltip?: boolean;
  /** 툴팁 텍스트 */
  tooltipNode?: string | React.ReactNode;
  /** 툴팁 부가 텍스트 (단축키 등) */
  tooltipSubNode?: string | React.ReactNode;
  /** 툴팁 위치 */
  tooltipPosition?: TooltipPosition;
}

/**
 * ToggleItem - 토글 버튼 컴포넌트
 *
 * ToggleGroup 내부에서 사용되며, Context를 통해 선택 상태를 관리합니다.
 * - 기본: 아이콘 전용 (28x28, padding 4px + icon 20px)
 * - label 설정 시: 아이콘 + 텍스트 레이아웃 (gap 2px)
 * - label + showIcon={false}: 텍스트 전용
 */
const ToggleItem = forwardRef<HTMLButtonElement, ToggleItemProps>(
  (
    {
      value,
      children,
      label,
      showIcon = true,
      className,
      disabled: itemDisabled,
      isShowTooltip = false,
      tooltipNode,
      tooltipSubNode,
      tooltipPosition = 'bottom',
      onClick,
      ...props
    },
    ref,
  ) => {
    const { disabled: groupDisabled, isPressed, onToggle } = useToggleGroup();

    const disabled = groupDisabled || itemDisabled;
    const pressed = isPressed(value);
    const hasLabel = !!label;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      onToggle(value);
      onClick?.(e);
    };

    const button = (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-pressed={pressed}
        disabled={disabled}
        className={cn(
          'flex items-center justify-center',
          'min-h-[28px] overflow-hidden rounded-3xs p-4xs',
          '[&_svg]:size-[20px]',
          'transition-all',
          hasLabel ? 'gap-5xs' : 'flex-col',
          pressed
            ? [
                'bg-comp-toggle-control-selected-bg',
                'shadow-[0_0_8px_rgba(4,4,5,0.08)]',
                '[&_path]:fill-comp-toggle-control-text-default',
              ]
            : [
                'bg-comp-toggle-control-unselected-bg-default',
                'hover:bg-comp-toggle-control-unselected-bg-hover',
                '[&_path]:fill-comp-toggle-control-text-default',
              ],
          disabled && [
            'cursor-not-allowed',
            '[&_path]:!fill-comp-toggle-control-text-disabled',
            pressed
              ? 'bg-comp-toggle-control-selected-bg shadow-none'
              : 'bg-comp-toggle-control-unselected-bg-default',
          ],
          className,
        )}
        onClick={handleClick}
        {...props}
      >
        {(!hasLabel || showIcon) && children}
        {hasLabel && (
          <span
            className={cn(
              'text-comp-moderate-small text-center leading-none text-comp-toggle-control-text-default',
              disabled && 'text-comp-toggle-control-text-disabled',
            )}
          >
            {label}
          </span>
        )}
      </button>
    );

    if (!isShowTooltip) return button;

    return (
      <RadixTooltipProvider delayDuration={100}>
        <RadixTooltip>
          <RadixTooltipTrigger asChild>{button}</RadixTooltipTrigger>
          <RadixTooltipContent side={tooltipPosition}>
            {tooltipNode && <TooltipText>{tooltipNode}</TooltipText>}
            {tooltipSubNode && (
              <TooltipSubText>{tooltipSubNode}</TooltipSubText>
            )}
          </RadixTooltipContent>
        </RadixTooltip>
      </RadixTooltipProvider>
    );
  },
);

ToggleItem.displayName = 'ToggleItem';

export default ToggleItem;
